/**
 * Pure audio playback logic for the listening player (Phase 2). Kept free of
 * React/DOM so it is unit-testable in isolation:
 *   - wordIndexAtMs: karaoke highlight lookup (which word is playing at time t)
 *   - createListenTracker: maps playback position → the SAME tracker events the
 *     text reader emits (STARTED_CHAPTER, quartiles, COMPLETED_CHAPTER,
 *     TIME_SPENT), so listening fills the same reader-progress fields with no
 *     new telemetry model (plan §7.3).
 */

// tracker event ids (mirror main-apps/author/src/utils/tracker.js — kept local
// so this pure module has no React/firebase import chain)
export const EVENT = {
  TIME_SPENT: 2,
  STARTED_CHAPTER: 3,
  COMPLETED_CHAPTER: 4,
  CHAPTER_FIRST_QUARTILE: 7,
  CHAPTER_MID_POINT: 8,
  CHAPTER_THIRD_QUARTILE: 9,
};

/**
 * Binary-search `timing.words` for the word playing at `ms`.
 * Returns the word index, or -1 when there is no timing / before the first word.
 * During an inter-word gap it holds on the most recent word that has started, so
 * the highlight never flickers off between words. Degrades safely on bad input.
 */
export const wordIndexAtMs = (words, ms) => {
  if (!Array.isArray(words) || words.length === 0) return -1;
  if (ms == null || ms < words[0].startMs) return -1;
  let lo = 0;
  let hi = words.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (words[mid].startMs <= ms) {
      best = mid; // last word that has started at/*before* ms
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
};

/**
 * Resolve a playback position `ms` to the SENTENCE anchor to attach a
 * listening-mode comment/reaction to (audiobook plan §6.2). Binary-searches
 * `timing.sentences` for the sentence whose [startMs, endMs) contains `ms`,
 * falling back to the most recent sentence that has started (so a comment made
 * in an inter-sentence gap still lands on the line just heard).
 *
 * Returns the sentence's `anchor` shape extended with the `audioMs` breadcrumb —
 * i.e. `{ startKey, startOffset, endKey, endOffset, sceneId?, audioMs }`, ready
 * to POST as `inlineInfo`. Returns null when there is no usable timing (missing
 * map, empty sentences, or before the first sentence) so the caller can degrade
 * to a chapter-level comment (§6.4).
 */
export const sentenceAnchorAtMs = (sentences, ms) => {
  if (!Array.isArray(sentences) || sentences.length === 0) return null;
  if (ms == null || !isFinite(ms) || ms < sentences[0].startMs) return null;
  let lo = 0;
  let hi = sentences.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sentences[mid].startMs <= ms) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (best < 0) return null;
  const anchor = sentences[best].anchor;
  // The server's InlineComment schema marks startKey/startOffset/endKey/endOffset
  // all required, so ALL four must be present (offsets legitimately being 0).
  // An incomplete anchor → null, so the caller degrades to a chapter-level
  // comment rather than POSTing a malformed inlineInfo that 500s.
  if (
    !anchor ||
    anchor.startKey == null ||
    anchor.startOffset == null ||
    anchor.endKey == null ||
    anchor.endOffset == null
  ) {
    return null;
  }
  return {
    startKey: anchor.startKey,
    startOffset: anchor.startOffset,
    endKey: anchor.endKey,
    endOffset: anchor.endOffset,
    ...(anchor.sceneId ? { sceneId: anchor.sceneId } : {}),
    audioMs: Math.round(ms),
  };
};

/**
 * Live read-along anchor for the manuscript. Prefer the exact narrated word
 * when ElevenLabs supplied global character offsets and the sentence lives in
 * one block; otherwise keep the whole narrated sentence highlighted. This is
 * intentionally separate from sentenceAnchorAtMs: comments/reactions remain
 * sentence-scoped even when the visual playhead can be word-precise.
 */
export const playbackAnchorAtMs = (timing, ms) => {
  const sentences = timing && timing.sentences;
  const sentenceAnchorWithMs = sentenceAnchorAtMs(sentences, ms);
  if (!sentenceAnchorWithMs) return null;
  const { audioMs, ...sentenceAnchor } = sentenceAnchorWithMs;

  let sentenceIndex = -1;
  if (Array.isArray(sentences)) {
    for (let i = 0; i < sentences.length; i += 1) {
      if (sentences[i].startMs <= ms) sentenceIndex = i;
      else break;
    }
  }
  const sentence = sentences && sentences[sentenceIndex];
  const words = timing && timing.words;
  const activeWordIndex = wordIndexAtMs(words, ms);
  const word = activeWordIndex >= 0 && words && words[activeWordIndex];

  const canMapWord =
    word &&
    sentence &&
    sentenceAnchor.startKey === sentenceAnchor.endKey &&
    Number.isFinite(word.charStart) &&
    Number.isFinite(word.charEnd) &&
    Number.isFinite(sentence.charStart) &&
    Number.isFinite(sentence.charEnd) &&
    word.charStart >= sentence.charStart &&
    word.charEnd <= sentence.charEnd;
  if (!canMapWord) return sentenceAnchor;

  const startOffset =
    sentenceAnchor.startOffset + (word.charStart - sentence.charStart);
  const endOffset = Math.min(
    sentenceAnchor.endOffset,
    sentenceAnchor.startOffset + (word.charEnd - sentence.charStart)
  );
  if (endOffset <= startOffset) return sentenceAnchor;

  return {
    startKey: sentenceAnchor.startKey,
    startOffset,
    endKey: sentenceAnchor.endKey,
    endOffset,
    ...(sentenceAnchor.sceneId ? { sceneId: sentenceAnchor.sceneId } : {}),
  };
};

/**
 * Forward (Listen → Read) scroll target: the sentence playing at `ms`, as a
 * plain anchor `{ startKey, startOffset, endKey, endOffset, sceneId? }` (no
 * audioMs breadcrumb — this is for scrolling/highlighting the text, not a
 * comment). Reuses the same sentence lookup as the feedback path (§6.2/§7.5).
 * Returns null when there's no usable timing so the caller keeps the current
 * scroll position.
 */
export const anchorForMs = (sentences, ms) => {
  const a = sentenceAnchorAtMs(sentences, ms);
  if (!a) return null;
  const { audioMs, ...anchor } = a; // drop the breadcrumb
  return anchor;
};

/**
 * Reverse (Read → Listen) index — the one NEW piece for the toggle (§7.5).
 * Maps the reader's current TEXT position to the audio `startMs` to begin
 * playback at, so switching modality lands on the same sentence.
 *
 * `position` is `{ key, offset }` in the same anchor space as
 * `timing.sentences[].anchor` (Draft block key / tiptap pid + char offset).
 * `orderedKeys` is the chapter's anchor keys in DOCUMENT order (as the reader
 * renders them) — needed to order a position that falls in a sentence-less
 * block (e.g. a blank line) so "nearest preceding sentence" is well-defined.
 * `words` is the optional `timing.words` list. When both word and sentence
 * character offsets exist, a position inside a single-block sentence is
 * refined to its nearest timed word.
 *
 * Returns the nearest word's `startMs` when it can be mapped safely; otherwise
 * the sentence CONTAINING the position, the NEAREST PRECEDING sentence, or the
 * first sentence's startMs (chapter start). Returns null only when there is no
 * usable timing, so the caller can start at the chapter beginning/current scroll.
 */
export const startMsForTextPosition = (
  sentences,
  position,
  orderedKeys = [],
  words = []
) => {
  if (!Array.isArray(sentences) || sentences.length === 0) return null;
  if (!position || position.key == null) return sentences[0].startMs;

  const keyIndex = new Map();
  (orderedKeys || []).forEach((k, i) => {
    if (!keyIndex.has(k)) keyIndex.set(k, i);
  });

  const posOffset = position.offset == null ? 0 : position.offset;
  const posDocIndex = keyIndex.has(position.key)
    ? keyIndex.get(position.key)
    : null;
  // Unknown key (not rendered / no ordering info) → can't place it; start at
  // the chapter beginning rather than guess.
  if (posDocIndex == null) return sentences[0].startMs;

  // Pick the sentence with the greatest doc-position that is <= the text
  // position (lexicographic on [docIndex, offset]). Robust to the sentences
  // array and orderedKeys disagreeing on order (they shouldn't, but don't
  // assume it).
  let best = null;
  let bestPos = null;
  for (const s of sentences) {
    const anchor = s && s.anchor;
    if (!anchor || anchor.startKey == null) continue;
    if (!keyIndex.has(anchor.startKey)) continue;
    const sDoc = keyIndex.get(anchor.startKey);
    const sOff = anchor.startOffset == null ? 0 : anchor.startOffset;
    const beforeOrAt =
      sDoc < posDocIndex || (sDoc === posDocIndex && sOff <= posOffset);
    if (!beforeOrAt) continue;
    if (
      bestPos == null ||
      sDoc > bestPos[0] ||
      (sDoc === bestPos[0] && sOff > bestPos[1])
    ) {
      best = s;
      bestPos = [sDoc, sOff];
    }
  }
  // position precedes every sentence → start at the chapter beginning
  if (!best) return sentences[0].startMs;

  // When ElevenLabs supplied global character offsets, refine the containing
  // sentence to the selected word. The timing map can only project this safely
  // when the sentence and selection live in one manuscript block; otherwise
  // sentence-level continuity remains the closest reliable fallback.
  const anchor = best.anchor;
  const canMapWord =
    anchor?.startKey === position.key &&
    anchor?.endKey === position.key &&
    posOffset >= (anchor.startOffset ?? 0) &&
    posOffset <= anchor.endOffset &&
    Number.isFinite(best.charStart) &&
    Number.isFinite(best.charEnd) &&
    Array.isArray(words);
  if (!canMapWord) return best.startMs;

  const targetChar =
    best.charStart + (posOffset - (anchor.startOffset ?? 0));
  const sentenceWords = words.filter(
    word =>
      Number.isFinite(word?.startMs) &&
      Number.isFinite(word?.charStart) &&
      Number.isFinite(word?.charEnd) &&
      word.charStart >= best.charStart &&
      word.charEnd <= best.charEnd
  );
  if (!sentenceWords.length) return best.startMs;

  const distanceFromTarget = word => {
    if (targetChar < word.charStart) return word.charStart - targetChar;
    if (targetChar >= word.charEnd) return targetChar - word.charEnd + 1;
    return 0;
  };
  const nearestWord = sentenceWords.reduce((nearest, word) =>
    distanceFromTarget(word) < distanceFromTarget(nearest) ? word : nearest
  );
  return nearestWord.startMs;
};

/**
 * Honest "listened" tracker. `advance(currentMs)` is called on the audio
 * element's timeupdate while playing; it accumulates only REAL forward playback
 * (a backward jump = seek → ignored; a large forward jump = scrub → clamped, so
 * skipping ahead doesn't fake progress). Quartile/complete fire off cumulative
 * listened time, so scrubbing to the end never marks a chapter "listened"
 * (the §7.3 honesty guard). Returns the event rows to emit for each call.
 *
 * `maxStepMs` bounds a single advance step (default 2s): timeupdate fires
 * ~4×/s, so a step larger than this is a seek, not playback.
 */
export const createListenTracker = ({ durationMs, maxStepMs = 2000 } = {}) => {
  let listenedMs = 0;
  let lastMs = null;
  let started = false;
  let completed = false;
  const firedQuartiles = new Set();
  // whole seconds of playback already reported as TIME_SPENT
  let reportedSeconds = 0;

  const QUARTILES = [
    { frac: 0.25, id: EVENT.CHAPTER_FIRST_QUARTILE },
    { frac: 0.5, id: EVENT.CHAPTER_MID_POINT },
    { frac: 0.75, id: EVENT.CHAPTER_THIRD_QUARTILE },
  ];

  const advance = currentMs => {
    const events = [];
    if (currentMs == null || !isFinite(currentMs)) return events;

    if (!started) {
      started = true;
      events.push({ id: EVENT.STARTED_CHAPTER });
    }

    if (lastMs != null) {
      const delta = currentMs - lastMs;
      // count only forward playback, clamp scrub jumps to nothing
      if (delta > 0 && delta <= maxStepMs) {
        listenedMs += delta;
      }
    }
    lastMs = currentMs;

    // TIME_SPENT accrues in whole seconds of ACTUAL playback (not wall-clock)
    const totalSeconds = Math.floor(listenedMs / 1000);
    if (totalSeconds > reportedSeconds) {
      events.push({
        id: EVENT.TIME_SPENT,
        count: totalSeconds - reportedSeconds,
      });
      reportedSeconds = totalSeconds;
    }

    if (durationMs > 0) {
      const frac = listenedMs / durationMs;
      for (const q of QUARTILES) {
        if (frac >= q.frac && !firedQuartiles.has(q.id)) {
          firedQuartiles.add(q.id);
          events.push({ id: q.id });
        }
      }
      if (frac >= 0.95 && !completed) {
        completed = true;
        events.push({ id: EVENT.COMPLETED_CHAPTER });
      }
    }
    return events;
  };

  // Definitive completion backstop for the audio element's `ended` event: the
  // browser only fires `ended` when playback truly reaches the end (never on a
  // seek), so this is an honest "listened to the end" signal — and it covers the
  // case where cumulative stalls kept `advance` from crossing the 0.95 slack.
  const markEnded = () => {
    if (!started || completed) return [];
    completed = true;
    return [{ id: EVENT.COMPLETED_CHAPTER }];
  };

  return {
    advance,
    markEnded,
    // exposed for tests / UI progress
    getListenedMs: () => listenedMs,
    isCompleted: () => completed,
  };
};
